import { useEffect, useState } from "react";
import api, { formatAxiosError } from "@/lib/api";
import { useDialog } from "@/context/DialogContext";
import {
  DownloadSimple, Upload, Warning, CheckCircle, HardDrives, Camera, ArrowClockwise,
  Trash, Clock,
} from "@phosphor-icons/react";

export default function BackupTab() {
  const { confirm } = useDialog();
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [confirmText, setConfirmText] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [restoreResult, setRestoreResult] = useState(null);

  // Snapshot state
  const [snapshots, setSnapshots] = useState([]);
  const [creatingSnap, setCreatingSnap] = useState(false);
  const [snapLabel, setSnapLabel] = useState("");
  const [snapNote, setSnapNote] = useState("");
  const [restoringSnap, setRestoringSnap] = useState(null); // snapshot being restored
  const [restoreConfirm, setRestoreConfirm] = useState("");

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: "", text: "" }), 6000);
  };

  const loadSnapshots = async () => {
    try {
      const r = await api.get("/backup/snapshots");
      setSnapshots(r.data.snapshots || []);
    } catch (e) {
      // Silent — user may not have caps in the future
    }
  };
  useEffect(() => { loadSnapshots(); }, []);

  const download = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/backup/download", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/gzip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.href = url;
      a.download = `velacin-backup-${stamp}.tar.gz`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      flash("ok", "Backup downloaded.");
    } catch (e) { flash("error", formatAxiosError(e)); }
    finally { setDownloading(false); }
  };

  const restore = async () => {
    if (!selectedFile) { flash("error", "Select a backup file first."); return; }
    if (confirmText !== "RESTORE") { flash("error", 'Type "RESTORE" to confirm.'); return; }
    setUploading(true); setRestoreResult(null);
    try {
      const fd = new FormData();
      fd.append("file", selectedFile);
      const r = await api.post("/backup/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRestoreResult(r.data);
      flash("ok", "Restore complete. You will be signed out — please log in again.");
      setTimeout(() => { window.location.href = "/login"; }, 3500);
    } catch (e) { flash("error", formatAxiosError(e)); }
    finally { setUploading(false); }
  };

  const createSnapshot = async () => {
    setCreatingSnap(true);
    try {
      await api.post("/backup/snapshots", { label: snapLabel.trim(), note: snapNote.trim() });
      setSnapLabel(""); setSnapNote("");
      await loadSnapshots();
      flash("ok", "Snapshot created and stored on the server.");
    } catch (e) { flash("error", formatAxiosError(e)); }
    finally { setCreatingSnap(false); }
  };

  const downloadSnapshot = async (s) => {
    try {
      const r = await api.get(`/backup/snapshots/${s.id}/download`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/gzip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = s.filename || "snapshot.tar.gz";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { flash("error", formatAxiosError(e)); }
  };

  const deleteSnapshot = async (s) => {
    const ok = await confirm({
      title: `Delete snapshot "${s.label}"?`,
      description: "This snapshot and any archived data it contains will be permanently removed.",
      confirmText: "Delete snapshot",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/backup/snapshots/${s.id}`);
      await loadSnapshots();
      flash("ok", "Snapshot deleted.");
    } catch (e) { flash("error", formatAxiosError(e)); }
  };

  const doRestoreSnapshot = async () => {
    if (!restoringSnap) return;
    if (restoreConfirm !== "RESTORE") return;
    setUploading(true);
    try {
      const r = await api.post(`/backup/snapshots/${restoringSnap.id}/restore`);
      setRestoreResult(r.data);
      flash("ok", "Snapshot restored. Redirecting to login…");
      setRestoringSnap(null); setRestoreConfirm("");
      setTimeout(() => { window.location.href = "/login"; }, 3500);
    } catch (e) { flash("error", formatAxiosError(e)); }
    finally { setUploading(false); }
  };

  const humanSize = (b) => {
    if (b == null) return "—";
    const u = ["B", "KB", "MB", "GB"];
    let i = 0, n = b;
    while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
  };

  return (
    <div className="max-w-5xl space-y-8">
      {msg.text && (
        <div className={`text-xs font-mono-mini uppercase tracking-widest px-3 py-2 border ${msg.type === "ok" ? "border-green-500 text-green-700 bg-green-50" : "border-red-500 text-red-700 bg-red-50"}`}>
          {msg.text}
        </div>
      )}

      {/* DOWNLOAD */}
      <section className="border border-border p-6">
        <div className="flex items-start gap-3 mb-4">
          <DownloadSimple size={20} weight="bold" className="text-[#002FA7] mt-1" />
          <div>
            <h3 className="font-display text-xl font-black mb-1">Download backup</h3>
            <p className="text-sm text-muted-foreground">
              Portable snapshot of the entire installation — all database tables, uploaded files,
              settings, roles and audit logs. Save this file somewhere safe.
            </p>
          </div>
        </div>
        <button onClick={download} disabled={downloading} className="btn-primary flex items-center gap-2 disabled:opacity-40" data-testid="backup-download">
          <DownloadSimple size={14} weight="bold" /> {downloading ? "Preparing…" : "Download backup (.tar.gz)"}
        </button>
      </section>

      {/* SERVER-SIDE SNAPSHOTS */}
      <section className="border border-border p-6">
        <div className="flex items-start gap-3 mb-4">
          <Camera size={20} weight="bold" className="text-[#002FA7] mt-1" />
          <div>
            <h3 className="font-display text-xl font-black mb-1">Server snapshots</h3>
            <p className="text-sm text-muted-foreground">
              Create snapshots and keep them on the server. Restore any version from this list in one click —
              no download or re-upload needed. Great for scheduled or pre-migration safety points.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <label className="block">
            <span className="block text-xs font-mono-mini uppercase tracking-widest text-muted-foreground mb-1">Label (optional)</span>
            <input className="w-full border border-border px-3 py-2 text-sm bg-white" value={snapLabel}
              onChange={(e) => setSnapLabel(e.target.value)} placeholder="e.g. Before migration, End of quarter"
              data-testid="snap-label" />
          </label>
          <label className="block">
            <span className="block text-xs font-mono-mini uppercase tracking-widest text-muted-foreground mb-1">Note (optional)</span>
            <input className="w-full border border-border px-3 py-2 text-sm bg-white" value={snapNote}
              onChange={(e) => setSnapNote(e.target.value)} placeholder="Any context or reason" />
          </label>
        </div>
        <button onClick={createSnapshot} disabled={creatingSnap} className="btn-primary flex items-center gap-2 disabled:opacity-40" data-testid="snap-create">
          <Camera size={14} weight="bold" /> {creatingSnap ? "Snapshotting…" : "Create snapshot"}
        </button>

        <div className="mt-6 border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted border-b border-border">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Label</th>
                <th className="text-left px-3 py-2 font-semibold">Created</th>
                <th className="text-left px-3 py-2 font-semibold">Size</th>
                <th className="text-left px-3 py-2 font-semibold">By</th>
                <th className="text-left px-3 py-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {snapshots.length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">No snapshots yet.</td></tr>
              ) : snapshots.map(s => (
                <tr key={s.id} className="border-b border-border" data-testid={`snap-row-${s.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold">{s.label}</div>
                    {s.note && <div className="text-xs text-muted-foreground">{s.note}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1"><Clock size={10} weight="bold" /> {new Date(s.created_at).toLocaleString()}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">{humanSize(s.size_bytes)}</td>
                  <td className="px-3 py-2 text-xs">{s.created_by_name || s.created_by_email || "—"}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      <button onClick={() => setRestoringSnap(s)} className="p-1 hover:bg-muted text-[#002FA7]" title="Restore" data-testid={`snap-restore-${s.id}`}>
                        <ArrowClockwise size={14} weight="bold" />
                      </button>
                      <button onClick={() => downloadSnapshot(s)} className="p-1 hover:bg-muted" title="Download" data-testid={`snap-download-${s.id}`}>
                        <DownloadSimple size={14} weight="bold" />
                      </button>
                      <button onClick={() => deleteSnapshot(s)} className="p-1 hover:bg-muted text-red-600" title="Delete" data-testid={`snap-del-${s.id}`}>
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* RESTORE from uploaded file */}
      <section className="border-2 border-red-500 p-6 bg-red-50/40">
        <div className="flex items-start gap-3 mb-4">
          <Warning size={20} weight="fill" className="text-red-600 mt-1" />
          <div>
            <h3 className="font-display text-xl font-black mb-1 text-red-800">Restore from backup file</h3>
            <p className="text-sm text-red-800">
              <b>This will wipe the current installation</b> — all users, projects, tasks, leaves, attendance,
              uploads, settings — and replace them with the contents of the uploaded backup. There is no undo.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <label className="block">
            <span className="block text-xs font-mono-mini uppercase tracking-widest text-muted-foreground mb-1">Backup file (.tar.gz)</span>
            <input type="file" accept=".tar.gz,.tgz,application/gzip,application/x-gzip"
              onChange={(e) => setSelectedFile(e.target.files && e.target.files[0])}
              className="block w-full border border-border px-3 py-2 text-sm bg-white" data-testid="backup-file-input" />
          </label>
          <label className="block">
            <span className="block text-xs font-mono-mini uppercase tracking-widest text-muted-foreground mb-1">Type &quot;RESTORE&quot; to confirm</span>
            <input type="text" value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
              placeholder="RESTORE" className="w-full border border-border px-3 py-2 text-sm bg-white" data-testid="backup-confirm-text" />
          </label>
          <button onClick={restore} disabled={uploading || confirmText !== "RESTORE" || !selectedFile}
            className="bg-red-600 text-white px-4 py-2 text-sm font-semibold flex items-center gap-2 hover:bg-red-700 disabled:opacity-40" data-testid="backup-restore-btn">
            <Upload size={14} weight="bold" /> {uploading ? "Restoring…" : "Restore from backup"}
          </button>
        </div>

        {restoreResult && (
          <div className="mt-6 border border-green-500 bg-green-50 p-4">
            <div className="flex items-center gap-2 text-green-800 font-semibold mb-2">
              <CheckCircle size={16} weight="fill" /> Restore complete
            </div>
            <div className="text-xs text-green-800 mb-2">Manifest:</div>
            <pre className="text-[10px] font-mono-mini bg-white border border-green-300 p-3 overflow-x-auto">{JSON.stringify(restoreResult.manifest, null, 2)}</pre>
          </div>
        )}
      </section>

      <section className="border border-border p-6">
        <div className="flex items-start gap-3">
          <HardDrives size={20} weight="bold" className="text-muted-foreground mt-1" />
          <div className="text-xs text-muted-foreground">
            <div className="font-semibold text-foreground mb-1">What&apos;s inside a backup?</div>
            <ul className="list-disc list-inside space-y-0.5">
              <li>All database tables (users, projects, tasks, leaves, attendance, timesheets, expenses, roles, referrals, jobs, issues, SSO, settings, audit logs)</li>
              <li>All uploaded files (avatars, task attachments, onboarding documents)</li>
              <li>Full role &amp; permissions matrix</li>
              <li>Branding, email, expense config, onboarding/offboarding config</li>
              <li>A manifest with document counts for verification</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Confirm dialog for snapshot restore */}
      {restoringSnap && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => { setRestoringSnap(null); setRestoreConfirm(""); }}>
          <div className="bg-white border-2 border-red-500 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-red-500 bg-red-50 flex items-center gap-2">
              <Warning size={18} weight="fill" className="text-red-600" />
              <div className="font-display text-lg font-black text-red-800">Restore snapshot</div>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm">
                You are about to restore <b>{restoringSnap.label}</b> (created {new Date(restoringSnap.created_at).toLocaleString()}).
                This will <b>wipe the current data</b> and replace it with the snapshot. Everyone will need to sign in again.
              </p>
              <label className="block">
                <span className="block text-xs font-mono-mini uppercase tracking-widest text-muted-foreground mb-1">Type &quot;RESTORE&quot; to confirm</span>
                <input type="text" value={restoreConfirm} onChange={(e) => setRestoreConfirm(e.target.value)}
                  className="w-full border border-border px-3 py-2 text-sm bg-white" data-testid="snap-restore-confirm" />
              </label>
            </div>
            <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
              <button onClick={() => { setRestoringSnap(null); setRestoreConfirm(""); }} className="px-4 py-2 text-sm border border-border hover:bg-muted">Cancel</button>
              <button onClick={doRestoreSnapshot} disabled={restoreConfirm !== "RESTORE" || uploading}
                className="bg-red-600 text-white px-4 py-2 text-sm font-semibold flex items-center gap-2 hover:bg-red-700 disabled:opacity-40" data-testid="snap-restore-confirm-btn">
                <ArrowClockwise size={14} weight="bold" /> {uploading ? "Restoring…" : "Restore this snapshot"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
